import { Fragment, useState, useEffect } from 'react'
import Layout from '../../components/Layout'
import api, { getOpenBetsForMarket, voidSingleOpenBet, deleteSportsBet } from '../../services/api'
import { API_ENDPOINTS } from '../../config/api'
import FancySettlementModal from '../../components/FancySettlementModal'
import SettlementOpenBets from '../../components/SettlementOpenBets'
import SettlementVoidModal from '../../components/SettlementVoidModal'

const LIMIT = 100
const DEFAULT_OFFSET = 0

const MARKETS_FOR_FANCY = [
  '1st Innings 6 Overs Line', '2nd Innings 6 Overs Line', '3rd Innings 6 Overs Line',
  '1st Innings 50 Overs Line', '2nd Innings 50 Overs Line', '3rd Innings 50 Overs Line',
  '1st Innings 40 Overs Line', '2nd Innings 40 Overs Line', '3rd Innings 40 Overs Line',
  '1st Innings 30 Overs Line', '2nd Innings 30 Overs Line', '3rd Innings 30 Overs Line',
  '1st Innings 20 Overs Line', '2nd Innings 20 Overs Line', '3rd Innings 20 Overs Line',
  '1st Innings 10 Overs Line', '2nd Innings 10 Overs Line', '3rd Innings 10 Overs Line',
  'Over By Over', 'Ball By Ball', 'Normal', 'khado', 'meter', 'fancy1', 'oddeven'
]

function getOrderedTabKeys(data) {
  if (!data || typeof data !== 'object') return []
  const keysWithData = Object.keys(data).filter(
    (k) => Array.isArray(data[k]) && data[k].length > 0
  )
  const ordered = []
  for (const name of MARKETS_FOR_FANCY) {
    if (keysWithData.includes(name)) ordered.push(name)
  }
  if (keysWithData.includes('others')) ordered.push('others')
  for (const k of keysWithData) {
    if (!ordered.includes(k)) ordered.push(k)
  }
  return ordered
}

export default function FancySettlement() {
  const [fancyData, setFancyData] = useState(null)
  const [tabKeys, setTabKeys] = useState([])
  const [activeTab, setActiveTab] = useState(null)
  const [loading, setLoading] = useState(false)
  const [modalShow, setModalShow] = useState(false)
  const [modalMode, setModalMode] = useState('declare')
  const [selectedRow, setSelectedRow] = useState(null)

  // Per-bet view: ek fancy market ki saari open bets, aur unme se kisi ek ka void
  const [expandedRow, setExpandedRow] = useState(null)
  const [openBets, setOpenBets] = useState([])
  const [betsLoading, setBetsLoading] = useState(false)
  const [voidConfirm, setVoidConfirm] = useState(null)
  const [voidSubmitting, setVoidSubmitting] = useState(false)
  const [voidError, setVoidError] = useState('')
  // Wahi confirm dialog dono kaam karta hai. deleteMode = Delete Bet, jo void
  // ki tarah paisa wapas karta hai aur uske baad bet ki row + statement trail
  // mita deta hai. Delete transaction password maangta hai (void nahi maangta).
  const [deleteMode, setDeleteMode] = useState(false)
  const [txnPassword, setTxnPassword] = useState('')

  const fetchFanmatches = async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const response = await api.get(API_ENDPOINTS.INTERNALSETTLE.FANMATCHES, {
        params: { limit: LIMIT, offset: DEFAULT_OFFSET }
      })
      if (response.data?.success && response.data.data && typeof response.data.data === 'object') {
        const data = response.data.data
        setFancyData(data)
        const ordered = getOrderedTabKeys(data)
        setTabKeys(ordered)
        setActiveTab((prev) => (ordered.length > 0 && !ordered.includes(prev) ? ordered[0] : prev))
      } else {
        setFancyData({})
        setTabKeys([])
      }
    } catch (error) {
      if (!silent) console.error('Error fetching fancy matches:', error)
      setFancyData({})
      setTabKeys([])
    } finally {
      if (!silent) setLoading(false)
    }
  }

  useEffect(() => {
    fetchFanmatches()
    const interval = setInterval(() => fetchFanmatches(true), 2000)
    return () => clearInterval(interval)
  }, [])

  const rows = (activeTab && fancyData && Array.isArray(fancyData[activeTab]))
    ? fancyData[activeTab]
    : []

  const handleDeclare = (row) => {
    setSelectedRow({ ...row, marketType: row.marketType || activeTab })
    setModalMode('declare')
    setModalShow(true)
  }

  const handleVoid = (row) => {
    setSelectedRow({ ...row, marketType: row.marketType || activeTab })
    setModalMode('void')
    setModalShow(true)
  }

  const handleSubmit = async () => {
    setModalShow(false)
    setSelectedRow(null)
    fetchFanmatches(true)
  }

  const getRowKey = (row) =>
    `${row.matchId}-${row.marketType || activeTab}-${row.gameType}-${row.selectionName || ''}`

  const handleExpand = async (row) => {
    const key = getRowKey(row)
    if (expandedRow === key) {
      setExpandedRow(null)
      setOpenBets([])
      return
    }
    setExpandedRow(key)
    setBetsLoading(true)
    try {
      const resp = await getOpenBetsForMarket({
        match_id: row.matchId,
        market_type: row.marketType || activeTab,
        game_type: row.gameType || 'FAN',
        selection_name: row.selectionName || undefined
      })
      setOpenBets(resp?.success ? resp.data : [])
    } catch {
      setOpenBets([])
    } finally {
      setBetsLoading(false)
    }
  }

  const handleVoidSingleBet = (bet) => {
    setVoidError('')
    setTxnPassword('')
    setDeleteMode(false)
    setVoidConfirm(bet)
  }

  const handleDeleteSingleBet = (bet) => {
    setVoidError('')
    setTxnPassword('')
    setDeleteMode(true)
    setVoidConfirm(bet)
  }

  const confirmVoidSingle = async () => {
    if (!voidConfirm) return
    setVoidSubmitting(true)
    setVoidError('')
    try {
      const resp = deleteMode
        ? await deleteSportsBet({ bet_id: voidConfirm.id, transactionPassword: txnPassword })
        : await voidSingleOpenBet({ bet_id: voidConfirm.id })
      if (resp?.success) {
        setVoidConfirm(null)
        setDeleteMode(false)
        setTxnPassword('')
        setExpandedRow(null)
        setOpenBets([])
        fetchFanmatches(true)
      } else {
        setVoidError(resp?.error || 'Unknown error')
      }
    } catch (err) {
      setVoidError(err?.response?.data?.error || err.message)
    } finally {
      setVoidSubmitting(false)
    }
  }

  return (
    <Layout title="Fancy Settlement - Diamond Admin">
      <div className="listing-grid">
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-flex align-items-center justify-content-between">
              <h4 className="mb-0 font-size-18">Fancy Settlement</h4>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => fetchFanmatches()}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="d-flex flex-wrap gap-2 mb-3">
              {tabKeys.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`btn btn-sm ${activeTab === key ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => { setActiveTab(key); setExpandedRow(null); setOpenBets([]) }}
                >
                  {key}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered table-sm mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="text-start">Match Title</th>
                        <th className="text-start">
                          {MARKETS_FOR_FANCY.includes(activeTab) ? 'Market Name' : 'Market Type'}
                        </th>
                        <th className="text-end">Total Bets</th>
                        <th className="text-center" style={{ minWidth: '240px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={4} className="text-center py-4">
                            Loading...
                          </td>
                        </tr>
                      ) : rows.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center py-4">
                            No matches found
                          </td>
                        </tr>
                      ) : (
                        rows.map((row, index) => (
                          <Fragment key={`${row.matchId}-${row.eventId}-${index}`}>
                            <tr>
                              <td className="text-truncate" style={{ maxWidth: '200px' }}>
                                {row.eventName || row.matchTitle || '-'}
                              </td>
                              <td className="fw-bold text-truncate" style={{ maxWidth: '180px' }}>
                                {MARKETS_FOR_FANCY.includes(activeTab)
                                  ? (row.selectionName || '-')
                                  : (row.marketType ?? activeTab ?? '-')}
                              </td>
                              <td className="text-end fw-bold">
                                {row.totalBets ?? 0}
                              </td>
                              <td className="text-center">
                                <div className="btn-group btn-group-sm">
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => handleDeclare(row)}
                                  >
                                    Declare
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => handleVoid(row)}
                                  >
                                    Void
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-warning"
                                    onClick={() => handleExpand(row)}
                                  >
                                    {expandedRow === getRowKey(row) ? 'Hide' : 'Per Bet'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {expandedRow === getRowKey(row) && (
                              <tr>
                                <td colSpan={4} className="bg-light p-3">
                                  <SettlementOpenBets
                                    label={row.selectionName || row.marketType || activeTab}
                                    bets={openBets}
                                    loading={betsLoading}
                                    onVoidBet={handleVoidSingleBet}
                                    onDeleteBet={handleDeleteSingleBet}
                                  />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FancySettlementModal
        show={modalShow}
        onHide={() => {
          setModalShow(false)
          setSelectedRow(null)
        }}
        mode={modalMode}
        row={selectedRow}
        onSubmit={handleSubmit}
      />

      <SettlementVoidModal
        show={!!voidConfirm}
        title={deleteMode ? 'Delete Bet — permanent' : 'Void Single Bet'}
        onHide={() => setVoidConfirm(null)}
        onConfirm={confirmVoidSingle}
        submitting={voidSubmitting}
        confirmLabel={deleteMode ? 'Delete Permanently' : 'Confirm Void'}
        requirePassword={deleteMode}
        password={txnPassword}
        onPasswordChange={setTxnPassword}
        error={voidError}
      >
        {voidConfirm && (
          <>
            <p className="mb-2">
              Void bet <strong>#{voidConfirm.id}</strong> for user <strong>{voidConfirm.username}</strong>?
            </p>
            <p className="mb-2">
              Selection: <strong>{voidConfirm.selectionName}</strong> · Stake:{' '}
              <strong>{Number(voidConfirm.stakeAmount || 0).toFixed(2)}</strong> · Odds:{' '}
              <strong>{voidConfirm.odds}</strong>
            </p>
            {deleteMode ? (
              <div className="alert alert-danger mb-0 py-2">
                The stake is refunded exactly like a void, and then{' '}
                <strong>the bet is deleted</strong> — it disappears from the user&apos;s bet
                history and statement completely. <strong>This cannot be undone.</strong> A copy
                is kept in the void audit and activity log only.
              </div>
            ) : (
              <p className="mb-0 text-muted">
                The stake will be refunded. This action cannot be undone.
              </p>
            )}
          </>
        )}
      </SettlementVoidModal>
    </Layout>
  )
}
